import { shallowEqual } from '#pplx/frontend/common/shallowEqual.js';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
function useShallowStableValue(value) {
    const valueRef = useRef(value);
    if (!shallowEqual(valueRef.current, value)) {
        valueRef.current = value;
    }
    return valueRef.current;
}
/**
 * Returns a debounced version of the input value.
 * The debounced value updates after the specified delay has passed without a
 * change, or after `maxWait` when one is provided and changes keep arriving.
 *
 * @param value - The value to debounce
 * @param delay - The debounce delay in milliseconds
 * @param options - Options matching lodash's debounce settings
 * @returns The debounced value
 */
export function useDebouncedValue(value, delay, options) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const stableOptions = useShallowStableValue(options);
    const debouncedSetValue = useMemo(() => {
        const setValue = (newValue) => setDebouncedValue(newValue);
        return debounce(setValue, delay, stableOptions);
    }, [delay, stableOptions]);
    useEffect(() => {
        debouncedSetValue(value);
    }, [value, debouncedSetValue]);
    useEffect(() => () => debouncedSetValue.cancel(), [debouncedSetValue]);
    return debouncedValue;
}
//# sourceMappingURL=useDebouncedValue.js.map